import React, { useState, useEffect } from 'react';
import { Sun, Moon, ChevronLeft, ChevronRight, ListFilter, BarChart3 } from 'lucide-react';
import ApiService from './ApiService';
import logo from './logo.svg';
import logoDark from './logo_dark.svg';
import { useTheme } from './ThemeContext';

const DashboardPage = () => {
  const { isDark, toggle: toggleTheme } = useTheme();
  const [contents, setContents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('normal'); // 'normal' or 'priority'
  const [showStats, setShowStats] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    fetchContents();
    fetchStats();
  }, [currentPage, viewMode]);

  const fetchStats = async () => {
    try {
      const response = await ApiService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchContents = async () => {
    try {
      setIsLoading(true);
      const response =
        viewMode === 'normal'
          ? await ApiService.getContents(currentPage - 1, pageSize)
          : await ApiService.getContentsByPriority(currentPage - 1, pageSize);
      setContents(response.data);
    } catch (error) {
      console.error('Failed to fetch contents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1) {
      setCurrentPage(newPage);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'available':
        return '#10B981';
      case 'deleted':
        return '#2e2e2e';
      case 'attributes_generation_issue':
        return '#EF4444';
      case 'processing':
        return '#3B82F6';
      case 'processing_issue':
        return '#EF4444';
      case 'error':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-white' : 'bg-gray-50 text-gray-800'}`}>
      {/* Header */}
      <header className={`${isDark ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <img className="max-h-10" src={isDark ? logoDark : logo} alt="Streamline Logo" />

          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowStats(!showStats)}
              className={`p-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              <BarChart3 size={20} />
            </button>

            <button
              onClick={() => setViewMode(viewMode === 'normal' ? 'priority' : 'normal')}
              className={`p-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
            >
              <ListFilter size={20} />
            </button>

            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg ${isDark ? 'bg-gray-700 text-yellow-400' : 'bg-gray-100 text-gray-600'}`}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Stats Panel */}
      {showStats && stats && (
        <div className={`${isDark ? 'bg-gray-900' : 'bg-white'} border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h3 className="text-sm font-medium mb-1">Queue Size</h3>
                <p className="text-2xl font-semibold">{stats.queueSize}</p>
              </div>
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h3 className="text-sm font-medium mb-1">Uploaded</h3>
                <p className="text-2xl font-semibold">{stats.uploadedCount}</p>
              </div>
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <h3 className="text-sm font-medium mb-1">Deleted</h3>
                <p className="text-2xl font-semibold">{stats.deleted}</p>
              </div>
              {stats.currentProcessing && (
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <h3 className="text-sm font-medium mb-2">Currently Processing</h3>
                  {/* Detailed info for the current processing entity */}
                  <div className="space-y-1 text-sm">
                    {Object.entries(stats.currentProcessing).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="font-medium capitalize">{key}:</span>
                        <span className="text-right break-all">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* View Mode Indicator */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold">
            {viewMode === 'normal' ? 'All Contents' : 'Priority Queue'}
          </h2>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {contents.map((content) => (
                <div
                  key={content.localId}
                  className={`rounded-lg shadow-lg p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} transition transform hover:scale-[1.02] hover:shadow-xl`}
                >
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <h3 className="text-lg font-medium mb-3 break-words">{content.name}</h3>
                      <div className="space-y-2 text-sm">
                        <p className={`break-words ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="font-medium">Type:</span> {content.contentType}
                        </p>
                        <p className={`break-words ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="font-medium">Uploader ID:</span> {content.uploadedBy}
                        </p>
                        <p className={`break-words ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="font-medium">Uploaded At:</span> {formatTimestamp(content.uploadedAt)}
                        </p>
                        <p className={`break-words ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="font-medium">Processing Task ID:</span> {content.processingTaskId}
                        </p>
                        <p className={`break-words ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="font-medium">Attribute Task ID:</span> {content.attributeTaskId}
                        </p>
                        <p className={`break-words ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="font-medium">Priority:</span> {content.priority}
                        </p>
                        {content.hookPayload && (
                          <p className={`break-words ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            <span className="font-medium">Hook Payload:</span> {content.hookPayload}
                          </p>
                        )}
                      </div>
                    </div>
                    <span
                      className="text-sm px-3 py-1 rounded-full mt-4 self-start"
                      style={{
                        backgroundColor: `${getStatusColor(content.contentStatus)}15`,
                        color: getStatusColor(content.contentStatus),
                      }}
                    >
                      {content.contentStatus}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'} ${
                  currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'
                }`}
              >
                <ChevronLeft size={20} />
              </button>

              <div className={`px-4 py-2 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                Page {currentPage}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                className={`p-2 rounded-lg ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200`}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
